import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function LicenseAgreement() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <i className="ri-arrow-left-line text-xl text-yellow-500"></i>
            <span className="text-lg font-semibold">Back to Home</span>
          </Link>
          <div className="text-yellow-500 font-bold text-xl">Amagi Poker</div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-500 to-yellow-300 bg-clip-text text-transparent">
              End User License Agreement
            </h1>
            <p className="text-gray-400">Last Updated: January 2025</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">1. License Grant</h2>
              <p className="text-gray-300 leading-relaxed">
                This End User License Agreement ("EULA") is a legal agreement between you and Amagi Poker&nbsp;&nbsp;for the use of the Amagi Poker mobile application and related services (collectively, the "Software"). By installing, accessing, or using the Software, you agree to be bound by the terms of this EULA. If you do not agree to these terms, do not install or use the Software.
              </p>
            </section>

            {/* License Scope */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">2. Scope of License</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">2.1 Grant of License</h3>
                  <p className="leading-relaxed">Subject to your compliance with this EULA, Amagi Poker&nbsp;&nbsp;grants you a limited, non-exclusive, non-transferable, revocable license to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                    <li>Download and install the Software on your personal mobile device</li>
                    <li>Access and use the Software for personal, non-commercial entertainment purposes</li>
                    <li>Receive automatic updates and upgrades to the Software</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">2.2 License Restrictions</h3>
                  <p className="leading-relaxed">This license does NOT permit you to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                    <li>Use the Software for commercial purposes without authorization</li>
                    <li>Distribute, sublicense, rent, lease, or lend the Software</li>
                    <li>Copy, modify, or create derivative works of the Software</li>
                    <li>Use the Software in any manner that violates applicable laws</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">3. Intellectual Property Rights</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">3.1 Ownership</h3>
                  <p className="leading-relaxed">The Software and all intellectual property rights therein are and shall remain the exclusive property of Amagi Poker&nbsp;. This includes but is not limited to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                    <li>Source code, object code, and executable files</li>
                    <li>Graphics, animations, audio, and visual elements</li>
                    <li>Game mechanics, algorithms, and logic</li>
                    <li>Trademarks, logos, and brand elements</li>
                    <li>Documentation and user interfaces</li>
                    <li>All updates, modifications, and enhancements</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">3.2 Copyright Notice</h3>
                  <p className="leading-relaxed font-semibold text-white">All rights reserved. The Software is protected by copyright laws and international copyright treaties, as well as other intellectual property laws and treaties.</p>
                </div>
              </div>
            </section>

            {/* Restrictions */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">4. Use Restrictions</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed font-semibold text-white">You expressly agree NOT to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Reverse Engineer:</strong> Decompile, disassemble, reverse engineer, or attempt to derive the source code of the Software</li>
                  <li><strong className="text-white">Modify:</strong> Modify, adapt, translate, or create derivative works based on the Software</li>
                  <li><strong className="text-white">Remove Notices:</strong> Remove, alter, or obscure any copyright, trademark, or other proprietary notices</li>
                  <li><strong className="text-white">Circumvent Protection:</strong> Bypass, disable, or circumvent any security features or access controls</li>
                  <li><strong className="text-white">Unauthorized Tools:</strong> Use bots, cheats, hacks, mods, or any unauthorized third-party software</li>
                  <li><strong className="text-white">Extract Components:</strong> Extract or separate any components of the Software for use in other applications</li>
                  <li><strong className="text-white">Commercial Use:</strong> Use the Software for any commercial purpose without express written permission</li>
                  <li><strong className="text-white">Redistribute:</strong> Distribute, publish, or make the Software available to third parties</li>
                  <li><strong className="text-white">Interfere:</strong> Interfere with or disrupt the Software or servers connected to the Software</li>
                </ul>
              </div>
            </section>

            {/* Updates */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">5. Updates and Modifications</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">5.1 Automatic Updates</h3>
                  <p className="leading-relaxed">The Software may automatically download and install updates, upgrades, patches, and additional features. These updates are subject to this EULA unless accompanied by a separate license agreement.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">5.2 Modifications</h3>
                  <p className="leading-relaxed">We reserve the right to modify, suspend, or discontinue any aspect of the Software at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">5.3 Version Requirements</h3>
                  <p className="leading-relaxed">We may require you to install the latest version of the Software to continue using the service. Older versions may be disabled or may not function properly.</p>
                </div>
              </div>
            </section>

            {/* Data Collection */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">6. Data Collection and Privacy</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">The Software may collect and transmit certain data about your device and usage patterns, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Device information (model, operating system, unique identifiers)</li>
                  <li>Usage statistics and gameplay data</li>
                  <li>Performance metrics and crash reports</li>
                  <li>Location data (if permitted)</li>
                </ul>
                <p className="leading-relaxed mt-4">This data is collected to improve the Software, provide support, and enhance user experience. For detailed information about data collection and use, please refer to our Privacy Policy.</p>
              </div>
            </section>

            {/* Third Party */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">7. Third-Party Components</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">7.1 Third-Party Software</h3>
                  <p className="leading-relaxed">The Software may include third-party software components that are subject to separate license terms. These components are licensed to you under their respective license agreements, which may be found in the Software documentation or notices.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">7.2 Third-Party Services</h3>
                  <p className="leading-relaxed">The Software may integrate with or link to third-party services (such as social media platforms or payment processors). Your use of such services is subject to their respective terms and privacy policies. We are not responsible for third-party services.</p>
                </div>
              </div>
            </section>

            {/* Warranty Disclaimer */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">8. Warranty Disclaimer</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed font-semibold text-white">THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED.</p>
                <p className="leading-relaxed">Amagi Poker disclaims all warranties, including but not limited to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Warranties of merchantability and fitness for a particular purpose</li>
                  <li>Warranties of non-infringement</li>
                  <li>Warranties that the Software will be uninterrupted, secure, or error-free</li>
                  <li>Warranties regarding the accuracy or reliability of results</li>
                  <li>Warranties that defects will be corrected</li>
                </ul>
                <p className="leading-relaxed mt-4">Some jurisdictions do not allow the exclusion of implied warranties, so the above exclusions may not apply to you.</p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">9. Limitation of Liability</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed font-semibold text-white">TO THE MAXIMUM EXTENT PERMITTED BY LAW, AMAGI POKER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SOFTWARE.</p>
                <p className="leading-relaxed">This includes but is not limited to damages for:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Loss of profits, data, or business opportunities</li>
                  <li>Device damage or malfunction</li>
                  <li>Loss of virtual currency or items</li>
                  <li>Interruption of service</li>
                  <li>Security breaches or unauthorized access</li>
                </ul>
                <p className="leading-relaxed mt-4">Our total liability shall not exceed the amount you paid for the Software in the 12 months preceding the claim, or $100, whichever is greater.</p>
              </div>
            </section>

            {/* Export Compliance */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">10. Export Compliance</h2>
              <p className="text-gray-300 leading-relaxed">
                The Software may be subject to export control laws and regulations. You agree to comply with all applicable export and import laws and regulations. You represent that you are not located in a country subject to embargo or trade restrictions, and that you are not on any government list of prohibited or restricted parties.
              </p>
            </section>

            {/* Termination */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">11. Termination</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">11.1 Termination by You</h3>
                  <p className="leading-relaxed">You may terminate this EULA at any time by uninstalling the Software and ceasing all use.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">11.2 Termination by Us</h3>
                  <p className="leading-relaxed">We may terminate this EULA immediately if you breach any term of this agreement. Upon termination, you must immediately cease all use of the Software and delete all copies.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">11.3 Effect of Termination</h3>
                  <p className="leading-relaxed">Upon termination, all rights granted to you under this EULA will immediately cease. Sections relating to intellectual property, disclaimers, limitations of liability, and general provisions shall survive termination.</p>
                </div>
              </div>
            </section>

            {/* Governing Law */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">12. Governing Law</h2>
              <p className="text-gray-300 leading-relaxed">
                This EULA shall be governed by and construed in accordance with the laws of the jurisdiction in which Amagi Poker operates, without regard to conflict of law principles. Any disputes arising from this EULA shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.
              </p>
            </section>

            {/* Entire Agreement */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">13. Entire Agreement</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">This EULA, together with our Terms of Service and Privacy Policy, constitutes the entire agreement between you and Amagi Poker regarding the Software and supersedes all prior agreements and understandings.</p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                  <li><strong className="text-white">Severability:</strong> If any provision is found invalid, the remaining provisions remain in effect</li>
                  <li><strong className="text-white">Waiver:</strong> Failure to enforce any right does not constitute a waiver</li>
                  <li><strong className="text-white">Assignment:</strong> You may not assign this EULA; we may assign without restriction</li>
                  <li><strong className="text-white">Amendments:</strong> We may modify this EULA by posting the updated version</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Back to Top */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-semibold rounded-full hover:bg-yellow-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-up-line"></i>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
